import {
  CheckCircleFilled,
  DownloadOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Button, Modal, Tag, Tooltip, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import {
  siteAssetVariantUrl,
  videoGenTaskOutputUrl,
  type KeyframeRole,
  type ShotGenerationSessionState,
  type ShotGenerationShotV1,
} from '../../api'
import { triggerBrowserDownload } from '../../../api/client'

const STATUS_CLASS: Record<string, string> = {
  pending: 'tc-badge-neutral',
  queued: 'tc-badge-warning',
  running: 'tc-badge-info',
  succeeded: 'tc-badge-success',
  failed: 'tc-badge-error',
  stale: 'tc-badge-warning',
  needs_regeneration: 'tc-badge-warning',
}

function formatTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
}

function ShotVideoCard({
  workspaceId,
  shot,
  busy,
  onGenerate,
  onRegenerate,
}: {
  workspaceId: string
  shot: ShotGenerationShotV1
  busy: boolean
  onGenerate: (shotId: string) => void
  onRegenerate: (shotId: string) => void
}) {
  const { t } = useTranslation(['video', 'actions'])
  const [inputOpen, setInputOpen] = useState(false)
  const isActive = shot.status === 'queued' || shot.status === 'running'
  const missingRoles = shot.required_roles.filter((r) => !shot.source_site_asset_ids[r])
  const canGenerate = (shot.status === 'pending' || shot.status === 'failed') && missingRoles.length === 0
  const canRegenerate = shot.status === 'succeeded' || shot.status === 'failed' || shot.status === 'needs_regeneration'
  const hasPlayableOutput =
    (shot.status === 'succeeded' || shot.status === 'needs_regeneration') && !!shot.task_id && !!shot.output_asset_id
  const roleLabel = (role: KeyframeRole) => t(`video:director.keyframes.roles.${role}`)
  const statusLabel = t(`video:director.shotGeneration.status.${shot.status}`, { defaultValue: shot.status })

  return (
    <div className="tc-vsg-card" data-testid={`shot-video-card-${shot.shot_id}`}>
      <div className="tc-vsg-card-head">
        <span className="tc-vdc-shot-badge">SHOT {shot.shot_id.replace(/^shot_?/i, '')}</span>
        <span className={`tc-badge ${STATUS_CLASS[shot.status] || 'tc-badge-neutral'}`}>
          {isActive && <LoadingOutlined style={{ marginRight: 4 }} />}
          {statusLabel}
        </span>
      </div>

      <div className="tc-vsg-card-meta">
        <span>
          {formatTime(shot.start_time)}–{formatTime(shot.end_time)} · {shot.duration_seconds.toFixed(1)}s
        </span>
        <span>{shot.aspect_ratio}</span>
      </div>
      <div className="tc-vsg-card-strategy">
        {t('video:director.shotGeneration.strategyLabel')}：
        {t(`video:director.shotGeneration.strategies.${shot.generation_strategy}`, { defaultValue: shot.generation_strategy })}
      </div>

      <div className="tc-vsg-keyframe-row">
        {shot.required_roles.map((role) => {
          const assetId = shot.source_site_asset_ids[role]
          return (
            <div key={role} className="tc-vsg-keyframe-thumb" title={roleLabel(role)}>
              {assetId ? (
                <img src={siteAssetVariantUrl(workspaceId, assetId, 'thumbnail')} alt={roleLabel(role)} />
              ) : (
                <div className="tc-vsg-keyframe-thumb-missing">
                  <WarningOutlined />
                </div>
              )}
              <span>{roleLabel(role)}</span>
            </div>
          )
        })}
      </div>

      <div className="tc-vsg-video-slot">
        {hasPlayableOutput ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video controls src={videoGenTaskOutputUrl(workspaceId, shot.task_id!)} className="tc-vsg-video" />
        ) : isActive ? (
          <div className="tc-vkf-card-image-placeholder">
            <LoadingOutlined className="tc-vdc-spin" /> {t('video:director.shotGeneration.generating')}
          </div>
        ) : shot.status === 'stale' ? (
          <div className="tc-vkf-card-image-placeholder">
            <WarningOutlined /> {t('video:director.shotGeneration.staleWaiting')}
          </div>
        ) : (
          <div className="tc-vkf-card-image-placeholder">{t('video:director.shotGeneration.notGenerated')}</div>
        )}
      </div>

      {missingRoles.length > 0 && (
        <div className="tc-vdc-panel-disabled-hint">
          {t('video:director.shotGeneration.keyframesNotReady', {
            roles: missingRoles.map((r) => roleLabel(r)).join('、'),
          })}
        </div>
      )}
      {shot.error_message && <div className="tc-vdc-error-banner">{shot.error_message}</div>}
      {shot.stale_reason && shot.status === 'stale' && <div className="tc-vdc-stale-banner">{shot.stale_reason}</div>}

      <div className="tc-vkf-card-actions">
        {canGenerate && (
          <Button size="small" type="primary" loading={busy} onClick={() => onGenerate(shot.shot_id)}>
            {t('video:director.shotGeneration.generateVideo')}
          </Button>
        )}
        {canRegenerate && (
          <Button size="small" icon={<ReloadOutlined />} loading={busy} onClick={() => onRegenerate(shot.shot_id)}>
            {t('video:director.keyframes.regenerate')}
          </Button>
        )}
        {hasPlayableOutput && (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => triggerBrowserDownload(`${videoGenTaskOutputUrl(workspaceId, shot.task_id!)}?download=true`)}
          >
            {t('actions:download')}
          </Button>
        )}
        <Button size="small" onClick={() => setInputOpen(true)}>
          {t('video:director.shotGeneration.viewInput')}
        </Button>
        {shot.error_message && (
          <Tooltip title={shot.error_message}>
            <Button size="small" danger icon={<ExclamationCircleOutlined />}>
              {t('video:director.shotGeneration.errorDetails')}
            </Button>
          </Tooltip>
        )}
      </div>

      <Modal
        open={inputOpen}
        onCancel={() => setInputOpen(false)}
        footer={null}
        title={t('video:director.shotGeneration.inputModalTitle', { shotId: shot.shot_id })}
      >
        <div className="tc-vsg-input-modal">
          <div>
            <strong>{t('video:director.shotGeneration.timeRange')}：</strong>
            {formatTime(shot.start_time)}–{formatTime(shot.end_time)}（{shot.duration_seconds.toFixed(1)}s）
          </div>
          <div>
            <strong>{t('video:director.shotGeneration.aspectRatio')}：</strong>
            {shot.aspect_ratio}
          </div>
          <div>
            <strong>{t('video:director.shotGeneration.strategyLabel')}：</strong>
            {t(`video:director.shotGeneration.strategies.${shot.generation_strategy}`, {
              defaultValue: shot.generation_strategy,
            })}
          </div>
          <div>
            <strong>{t('video:director.shotGeneration.motionDescription')}：</strong>
            {shot.prompt_semantic || '—'}
          </div>
          <div>
            <strong>{t('video:director.shotGeneration.keyframeAssetIds')}：</strong>
            <ul>
              {shot.required_roles.map((role) => (
                <li key={role}>
                  {roleLabel(role)}: {shot.source_site_asset_ids[role] || t('video:director.shotGeneration.missing')}
                </li>
              ))}
            </ul>
          </div>
          {shot.task_id && (
            <div>
              <strong>Task ID：</strong>
              {shot.task_id}
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default function ShotGenerationPanel({
  workspaceId,
  loading,
  state,
  busyShotId,
  generatingAll,
  onGenerate,
  onRegenerate,
  onGenerateAll,
  disabledReason,
}: {
  workspaceId: string
  loading: boolean
  state: ShotGenerationSessionState | null
  busyShotId: string | null
  generatingAll: boolean
  onGenerate: (shotId: string) => void
  onRegenerate: (shotId: string) => void
  onGenerateAll: () => void
  disabledReason: string | null
}) {
  const { t } = useTranslation('video')

  if (loading) {
    return (
      <div className="tc-vdc-panel-empty" data-testid="shot-generation-panel-loading">
        <div className="tc-vdc-panel-empty-title">
          <LoadingOutlined className="tc-vdc-spin" /> {t('director.shotGeneration.loading')}
        </div>
      </div>
    )
  }

  if (disabledReason) {
    return <div className="tc-vdc-panel-empty">{disabledReason}</div>
  }

  if (!state || !state.gate_ok) {
    return (
      <div className="tc-vdc-panel-empty">
        {state?.gate_reason || t('director.shotGeneration.gateDefault')}
      </div>
    )
  }

  const shots = state.shots
  const preparedKeyframes = shots.reduce(
    (acc, s) => acc + s.required_roles.filter((r) => !!s.source_site_asset_ids[r]).length,
    0,
  )
  const totalKeyframes = shots.reduce((acc, s) => acc + s.required_roles.length, 0)
  const readyToGenerateCount = shots.filter(
    (s) =>
      (s.status === 'pending' || s.status === 'failed') && s.required_roles.every((r) => !!s.source_site_asset_ids[r]),
  ).length
  const anyActive = shots.some((s) => s.status === 'queued' || s.status === 'running')

  return (
    <div className="tc-vdc-storyboard-panel">
      <div className="tc-vsg-overview">
        <div className="tc-vsg-overview-item">
          <div className="tc-vsg-overview-label">{t('director.shotGeneration.overview.totalDuration')}</div>
          <div className="tc-vsg-overview-value">{state.total_duration_seconds.toFixed(1)}s</div>
        </div>
        <div className="tc-vsg-overview-item">
          <div className="tc-vsg-overview-label">{t('director.shotGeneration.overview.aspectRatio')}</div>
          <div className="tc-vsg-overview-value">{state.aspect_ratio}</div>
        </div>
        <div className="tc-vsg-overview-item">
          <div className="tc-vsg-overview-label">{t('director.shotGeneration.overview.shotCount')}</div>
          <div className="tc-vsg-overview-value">{state.shot_count}</div>
        </div>
        <div className="tc-vsg-overview-item">
          <div className="tc-vsg-overview-label">{t('director.shotGeneration.overview.preparedKeyframes')}</div>
          <div className="tc-vsg-overview-value">
            {preparedKeyframes}/{totalKeyframes}
          </div>
        </div>
        <div className="tc-vsg-overview-item">
          <div className="tc-vsg-overview-label">{t('director.shotGeneration.overview.videosToGenerate')}</div>
          <div className="tc-vsg-overview-value">{shots.length}</div>
        </div>
      </div>

      <div className="tc-vkf-shots">
        {shots.map((shot) => (
          <ShotVideoCard
            key={shot.shot_id}
            workspaceId={workspaceId}
            shot={shot}
            busy={busyShotId === shot.shot_id}
            onGenerate={onGenerate}
            onRegenerate={onRegenerate}
          />
        ))}
      </div>

      <div className="tc-vdc-panel-regen-row">
        <Button
          type="primary"
          icon={anyActive ? <LoadingOutlined /> : <PlayCircleOutlined />}
          loading={generatingAll}
          disabled={anyActive || readyToGenerateCount === 0}
          onClick={() => {
            if (readyToGenerateCount === 0) {
              message.warning(t('director.shotGeneration.noReadyShots'))
              return
            }
            onGenerateAll()
          }}
        >
          {t('director.shotGeneration.generateAll', { count: readyToGenerateCount })}
        </Button>
        {shots.every((s) => s.status === 'succeeded') && (
          <Tag icon={<CheckCircleFilled />} color="success">
            {t('director.shotGeneration.allGenerated')}
          </Tag>
        )}
      </div>
    </div>
  )
}
